/**
 * Canonical design tokens for the whole app.
 *
 * This is the single source of truth for color/type/spacing/radius. The
 * values below intentionally mirror what was already the de facto standard
 * (App.css's `:root` CSS vars and main.tsx's AntD ConfigProvider tokens, both
 * anchored on teal `#4F46E5`) so adopting this file causes no visual jump.
 * `src/styles/theme.ts` re-exports from here for backward compatibility —
 * import from THIS file in new code.
 *
 * `tokens.portal` was documented as "a deliberately distinct sub-palette (blue accent)
 * used only by the public-facing candidate/recruiter portal pages". That was never true in
 * the way it read: nothing ever imported `tokens.portal.primary` — the pages hardcoded
 * `#0071BD` in 87 files instead, so the "decision" existed only as this comment.
 *
 * As of 2026-09-01 the CANDIDATE-FACING pages (the portal, preboarding, the public careers
 * page, the advert, the apply form and the invite page) follow the Novara palette above,
 * because a candidate is looking at Novara and should see Novara. The blue survives on
 * internal screens that still hardcode it; those are a separate, larger change.
 *
 * As of 2026-09-08 that is finally true of the portal itself rather than only stated here. The
 * six candidate pages held 88 hardcoded colours and read NOTHING from this file - among them
 * three different gradient banners that appeared on no other screen in the product, and colours
 * (#8b5cf6, #02CA8F, #43a194, #6366f1) that are not in the palette at all. They now use the
 * tokens above and the gradients are gone, so the two `gradient` entries this block used to
 * carry have been deleted rather than left as a temptation.
 *
 * What is still read from here is the SEMANTIC block only — `textMuted`/`success`/
 * `warning`/`danger`, used by `scoreColor` below. Treat the rest as reference values for
 * the client portal, not as a palette anything consumes.
 */
export const tokens = {
  color: {
    primary: '#4F46E5',
    primaryDark: '#4338CA',
    primaryDarker: '#3730A3',
    primaryLight: '#E0E7FF',
    primaryLighter: '#EEF2FF',

    secondary: '#3498DB',
    secondaryDark: '#2980B9',
    secondaryLight: '#EBF5FB',

    success: '#27ae60',
    successLight: '#EAFAF1',
    warning: '#F39C12',
    warningLight: '#FEF9EC',
    danger: '#E74C3C',
    dangerLight: '#FDEDEC',
    info: '#3498DB',
    infoLight: '#EBF5FB',
    purple: '#8E44AD',
    purpleLight: '#F5EEF8',

    textPrimary: '#2C3E50',
    textSecondary: '#7F8C8D',
    textLight: '#95A5A6',
    textMuted: '#BDC3C7',
    textWhite: '#FFFFFF',

    bgMain: '#FFFFFF',
    bgLayout: '#F5F7FA',
    bgHover: '#F8F9FA',

    borderLight: '#E8ECEF',
    borderMedium: '#D1D8DD',

    white: '#FFFFFF',
  },
  radius: {
    sm: 6,
    md: 8,
    lg: 12,
    xl: 16,
    xxl: 20,
    full: 9999,
  },
  spacing: {
    xs: 4,
    sm: 8,
    md: 12,
    lg: 16,
    xl: 24,
    xxl: 32,
    xxxl: 48,
  },
  shadow: {
    xs: '0 1px 2px rgba(0, 0, 0, .04)',
    sm: '0 1px 3px rgba(0, 0, 0, .06)',
    md: '0 4px 6px rgba(0, 0, 0, .07)',
    lg: '0 10px 15px rgba(0, 0, 0, .1)',
    primary: '0 4px 14px rgba(79, 70, 229, .25)',
  },
  type: {
    fontFamily: "'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    headingFontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    size: {
      xs: 11,
      sm: 13,
      base: 14,
      md: 15,
      lg: 16,
      xl: 18,
      xxl: 20,
      xxxl: 24,
      display: 30,
    },
    weight: {
      normal: 400,
      medium: 500,
      semibold: 600,
      bold: 700,
    },
    /**
     * Fluid sizes for headline NUMBERS only (KPI tiles, widget totals). They scale with the width
     * of the card they sit in (`cqi` = 1% of the nearest size container's inline size) and are
     * capped both ways, so a number never shrinks below readable nor balloons on a wide screen.
     * The CSS variables are defined in `src/styles/foundation.css`; body text stays on `size`.
     * Outside a size container `cqi` falls back to the viewport, so the clamp still holds.
     */
    fluid: {
      metric: 'var(--fs-metric)',
      metricSm: 'var(--fs-metric-sm)',
    },
  },
  /** One chart height for dashboard widgets, instead of 220/200/190 scattered per widget. */
  chart: {
    height: 220,
    compact: 180,
  },
  /** Public candidate/recruiter portal — intentionally blue, not teal. */
  portal: {
    primary: '#0071BD',
    primaryDarker: '#005a9a',
    teal: '#4F46E5',
    tealDark: '#43a194',
    purple: '#8b5cf6',
    success: '#02CA8F',
    warning: '#faad14',
    danger: '#ff4d4f',
    textPrimary: '#111827',
    textSecondary: '#475569',
    textMuted: '#94a3b8',
    bgPage: '#f8fafc',
    bgCard: '#ffffff',
    border: '#e2e8f0',
    radius: { sm: 6, md: 10, lg: 16 },
  },
} as const;

/** Color a fit-assessment percentage (0-100) by health band. */
export function fitScoreColor(score: number | null | undefined): string {
  if (score == null) return tokens.portal.textMuted;
  if (score >= 75) return tokens.portal.success;
  if (score >= 50) return tokens.portal.warning;
  return tokens.portal.danger;
}
